// The supplier portal, supplier side: a separate small app at /supplier with no Microsoft sign-in. An emailed link
// starts a session; suppliers see their own orders, accept them, propose a new date, a split, a quantity or a price,
// say they can't supply a line, message the buyer, and manage who at their company has access.
// It is built from the same pages as the rest of CourageMFG: the top bar, a navigation row, a command bar with the
// actions, a list page with views and a table, and a record page with sections (General, Lines, Messages, History).
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Route, Routes, useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Mark } from "../ui";
import { ApiError } from "../api";
import { CommandBar } from "../Shell";
import { XSection } from "../crm";
import { ext, usDate, shortDate, fmtQty, money, daysBetween, REASON, STATE_SUPPLIER, EVENT, errText, type ExtMe, type ExtPo, type ExtPoRow, type Split } from "./spApi";
import "./portal.css";

export function SupplierApp() {
  return (
    <Routes>
      <Route path="sign-in" element={<SignIn />} />
      <Route path="auth/:token" element={<Verify />} />
      <Route path="preview/:token" element={<PreviewStart />} />
      <Route path="act/:token" element={<OneClick />} />
      <Route path="*" element={<Signed />} />
    </Routes>
  );
}

/** An action a buyer's preview blocked on purpose. */
const isPreview = (e: unknown) => e instanceof ApiError && e.body?.code === "preview";
const today = () => new Date().toISOString().slice(0, 10);
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

function useCompany() {
  const [name, setName] = useState<string>("");
  useEffect(() => { ext.info().then((r) => setName(r.company)).catch(() => {}); }, []);
  return name;
}

/** The top bar: the customer's name, as on every CourageMFG page, with the supplier's own name and sign-out at the right. */
function TopBar({ company, right }: { company: string; right?: ReactNode }) {
  return (
    <header className="topbar xp-topbar">
      <Link to="/supplier" className="brand"><Mark /><span>{company || "Supplier portal"}</span></Link>
      <span className="company xp-hide-m">Supplier portal</span>
      <span className="xp-grow" />
      {right}
    </header>
  );
}

/** Pages a supplier sees before signing in: the top bar and one short form. */
function Plain({ company, title, children }: { company: string; title: string; children: ReactNode }) {
  return (
    <div className="frame bare xp-frame">
      <TopBar company={company} />
      <main className="main">
        <div className="page xp-narrow">
          <section className="card">
            <div className="card-head"><h2>{title}</h2></div>
            <div className="card-body xp-body">{children}</div>
          </section>
        </div>
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Signing in
// ---------------------------------------------------------------------------------------------------------------
function SignIn({ note }: { note?: string }) {
  const company = useCompany();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<{ message: string; devLink?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { setSent(await ext.start(email)); } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };
  return (
    <Plain company={company} title="Sign in">
      <p className="muted">{company ? `${company}'s` : "Our"} supplier portal: confirm purchase orders, propose new dates and message our buyers. There's no password; we email you a link.</p>
      {note && <div className="msg warn">{note}</div>}
      {sent ? <>
        <div className="msg info">{sent.message}</div>
        {sent.devLink && <p className="muted sm">Dev mode: <a href={sent.devLink.replace(/^https?:\/\/[^/]+/, "")}>open the sign-in link</a></p>}
        <button className="btn" onClick={() => setSent(null)}>Use a different address</button>
      </> : (
        <form onSubmit={submit}>
          <div className="field"><label htmlFor="xp-email">Your work email</label><input id="xp-email" className="input" type="email" autoComplete="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          {err && <div className="msg error">{err}</div>}
          <button className="btn primary" disabled={busy}>{busy ? "Sending…" : "Email me a sign-in link"}</button>
        </form>
      )}
      <p className="muted sm">Not set up yet? Ask your contact in our purchasing team to invite you.</p>
    </Plain>
  );
}

function Verify() {
  const { token = "" } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const next = sp.get("next");
    const go = () => nav(next && next.startsWith("/supplier") ? next : "/supplier", { replace: true });
    // Already signed in on this device (links in emails are single use): just go.
    ext.me().then(go).catch(() => ext.verify(token).then(go).catch((e) => setErr(errText(e))));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <SignIn note={err} />;
  return <Plain company="" title="Signing in"><p className="muted">Signing you in…</p></Plain>;
}

/** A buyer's preview, opened from CourageMFG: starts a read-only session, then shows the supplier's view. */
function PreviewStart() {
  const { token = "" } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [err, setErr] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const next = sp.get("next");
    ext.preview(token).then(() => nav(next && next.startsWith("/supplier") ? next : "/supplier", { replace: true })).catch((e) => setErr(errText(e)));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Plain company="" title={err ? "The preview didn't open" : "Opening the preview"}>
      {err ? <><div className="msg error">{err}</div><a className="btn" href="/supplier-portal/suppliers">Back to suppliers</a></> : <p className="muted">Opening the preview…</p>}
    </Plain>
  );
}

/** A one-click link from an email: says what it will do, and does it on the button. */
function OneClick() {
  const { token = "" } = useParams();
  const [d, setD] = useState<Awaited<ReturnType<typeof ext.act>> | null>(null);
  const [err, setErr] = useState<{ text: string; company?: string } | null>(null);
  const [done, setDone] = useState<number[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { ext.act(token).then(setD).catch((e) => setErr({ text: errText(e), company: e instanceof ApiError ? e.body?.company : undefined })); }, [token]);
  const go = async () => { setBusy(true); try { setDone((await ext.doAct(token)).accepted); } catch (e) { setErr({ text: errText(e), company: d?.company }); } setBusy(false); };
  const company = d?.company ?? err?.company ?? "";
  const waiting = d?.lines.filter((l) => l.state === "sent" || l.state === "countered") ?? [];
  const open = (no: string, label: string, primary = false) => <Link className={`btn ${primary ? "primary" : ""}`} to={`/supplier/po/${encodeURIComponent(no)}`}>{label}</Link>;
  if (err) return <Plain company={company} title="That didn't work"><div className="msg error">{err.text}</div><Link className="btn primary" to="/supplier">Open the supplier portal</Link></Plain>;
  if (!d) return <Plain company="" title="Purchase order"><p className="muted">Loading…</p></Plain>;
  if (done) return <Plain company={company} title="Thank you"><div className="msg info">You've confirmed {done.length === 1 ? "line" : "lines"} {done.join(", ")} of {d.poNo}. Our buyer can see it now.</div><p className="muted">To add your order number or a message, open the order.</p>{open(d.poNo, `Open ${d.poNo}`)}</Plain>;
  if (d.used) return <Plain company={company} title="Already done"><p className="muted">This link was already used.</p>{open(d.poNo, `Open ${d.poNo}`, true)}</Plain>;
  if (!waiting.length) return <Plain company={company} title={d.poNo}><p className="muted">Nothing here is waiting for your answer.</p>{open(d.poNo, "Open the order", true)}</Plain>;
  return (
    <Plain company={company} title={`Accept ${d.lineNo ? `line ${d.lineNo} of ` : ""}${d.poNo}?`}>
      <p className="muted">You're confirming you'll deliver {waiting.length === 1 ? "this line" : "these lines"} by the date shown, at the price shown.</p>
      <div className="table-wrap"><table className="dgrid compact">
        <thead><tr><th>Part</th><th className="r">Quantity</th><th>Needed by</th><th className="r">Unit price</th></tr></thead>
        <tbody>{waiting.map((l) => <tr key={l.lineNo}><td><b>{l.vendorItemNo || l.itemNo}</b> <span className="muted">{l.description}</span></td><td className="r">{fmtQty(l.outstanding)} {l.uom}</td><td>{usDate(l.requestedDate)}</td><td className="r">{money(l.unitCost, d.currency)}</td></tr>)}</tbody>
      </table></div>
      <div className="btn-row xp-gap"><button className="btn primary" onClick={go} disabled={busy}>{busy ? "Confirming…" : "Yes, accept"}</button>{open(d.poNo, "No, I need to change something")}</div>
      <p className="muted sm">As {d.email}</p>
    </Plain>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Signed in: the frame every page sits in
// ---------------------------------------------------------------------------------------------------------------
function Signed() {
  const [me, setMe] = useState<ExtMe | null>(null);
  const [out, setOut] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const loc = useLocation();
  useEffect(() => { ext.me().then(setMe).catch((e) => setOut(e instanceof ApiError && e.status === 403 ? errText(e) : "")); }, [tick, loc.pathname]);
  if (out !== null) return <SignIn note={out || undefined} />;
  if (!me) return <Plain company="" title="Supplier portal"><p className="muted">Loading…</p></Plain>;
  const signOut = async () => { await ext.signOut().catch(() => {}); setOut(""); };
  const closePreview = async () => { await ext.signOut().catch(() => {}); window.close(); window.location.assign(`/supplier-portal/suppliers/${encodeURIComponent(me.supplier.vendorNo)}`); };
  const onTeam = loc.pathname.startsWith("/supplier/team");
  return (
    <>
      {me.preview && <div className="xp-pv" role="status">
        <span>Previewing as <b>{me.supplier.name}</b></span>
        <span className="meta">{me.preview.notInPortal ? "Not invited yet · their open orders in Business Central now" : "What they see today"} · look only, nothing is sent or saved</span>
        <span className="xp-grow" />
        <button className="stop" onClick={closePreview}>Close preview</button>
      </div>}
      <div className="frame xp-frame">
        <TopBar company={me.company} right={!me.preview && <button className="top-btn" onClick={signOut} title={me.user.email}>Sign out</button>} />
        <nav className="areanav" aria-label="Supplier portal">
          <span className="anav-area xp-who">{me.supplier.name}</span>
          <Link to="/supplier" className={`anav-item ${onTeam ? "" : "active"}`}>Purchase orders</Link>
          <Link to="/supplier/team" className={`anav-item ${onTeam ? "active" : ""}`}>Team</Link>
        </nav>
        <main className="main">
          <Routes>
            <Route path="po/:no" element={<PoPage me={me} onChange={() => setTick((x) => x + 1)} />} />
            <Route path="team" element={<Team me={me} onChange={() => setTick((x) => x + 1)} />} />
            <Route path="*" element={<Orders me={me} />} />
          </Routes>
        </main>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Purchase orders: the list
// ---------------------------------------------------------------------------------------------------------------
function orderStatus(p: ExtPoRow): [string, string] {
  if (p.status === "cancelled") return ["Cancelled", ""];
  if (p.status === "closed") return ["Closed", ""];
  if (p.status === "on_hold") return ["Being changed by us", "warn"];
  if (p.changed) return ["Changed by us: please confirm again", "warn"];
  if (p.answer) return ["Needs your answer", "warn"];
  if (p.late) return ["Late", "danger"];
  if (p.confirmDue) return ["Please reconfirm", "warn"];
  if (p.withBuyer) return ["With our buyer", ""];
  return ["Confirmed", "ok"];
}

function Orders({ me }: { me: ExtMe }) {
  const [params, setParams] = useSearchParams();
  const needs = me.counts.answer + me.counts.late + me.counts.confirmDue;
  const view = (params.get("view") as "action" | "open" | "closed" | null) ?? (needs ? "action" : "open");
  const [rows, setRows] = useState<ExtPoRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setRows(null); setErr(null); ext.pos(view).then(setRows).catch((e) => setErr(errText(e))); }, [view]);
  const set = (v: string) => setParams(new URLSearchParams({ view: v }), { replace: true });
  const status = [me.counts.answer ? `${me.counts.answer} line${me.counts.answer === 1 ? "" : "s"} to answer` : "Nothing to answer",
    me.counts.late ? `${me.counts.late} late` : "", me.counts.withBuyer ? `${me.counts.withBuyer} with our buyer` : "", `${me.counts.open} open line${me.counts.open === 1 ? "" : "s"}`].filter(Boolean).join(" · ");
  return (
    <>
      <CommandBar status={status}>
        <button className="cmd" onClick={() => { setRows(null); ext.pos(view).then(setRows).catch((e) => setErr(errText(e))); }}>Refresh</button>
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Purchase orders</h1><span className="sub">Orders from {me.company}{me.company.endsWith(".") ? "" : "."} Open one to accept it, propose a new date or tell us you can't supply a line.</span></div>
        <div className="pivot" role="tablist">
          <button role="tab" aria-selected={view === "action"} onClick={() => set("action")}>Needs your answer <span className="muted">{needs}</span></button>
          <button role="tab" aria-selected={view === "open"} onClick={() => set("open")}>All open</button>
          <button role="tab" aria-selected={view === "closed"} onClick={() => set("closed")}>Closed</button>
        </div>
        {err && <div className="msg error">{err}</div>}
        <section className="card">
          {!rows ? <div className="empty">Loading…</div> : !rows.length ? <div className="empty">{view === "action" ? "Nothing needs your answer. New orders and changes show up here, and we email you." : "No orders here."}</div> : (
            <div className="table-wrap">
              <table className="dgrid xp-orders">
                <thead><tr><th>Purchase order</th><th className="xp-hide-m">Ordered</th><th className="r xp-hide-m">Lines</th><th className="r">To answer</th><th>Next date</th><th className="r xp-hide-m">Value</th><th>Status</th></tr></thead>
                <tbody>{rows.map((p) => {
                  const [st, tone] = orderStatus(p);
                  return (
                    <tr key={p.poNo}>
                      <td><Link className="num" to={`/supplier/po/${encodeURIComponent(p.poNo)}`}>{p.poNo}</Link><div className="xp-m muted sm">{shortDate(p.orderDate)} · {money(p.value, p.currency)}</div></td>
                      <td className="xp-hide-m">{usDate(p.orderDate)}</td>
                      <td className="r xp-hide-m">{p.lines}</td>
                      <td className="r">{p.answer ? <b>{p.answer}</b> : <span className="muted">–</span>}</td>
                      <td>{usDate(p.nextDate)}</td>
                      <td className="r xp-hide-m">{money(p.value, p.currency)}</td>
                      <td><span className={`status ${tone}`}>{st}</span></td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// One purchase order: a record page
// ---------------------------------------------------------------------------------------------------------------
type Ln = ExtPo["lines"][number];
type Flash = { ok: boolean; text: string; preview?: boolean } | null;

function lineStatus(l: Ln): [string, string] {
  if (l.askConfirm) return ["Please reconfirm", "warn"];
  if (l.state === "confirmed" && l.promisedDate && l.promisedDate < today() && l.outstanding > 0) return ["Late", "danger"];
  const tone = l.state === "sent" || l.state === "countered" ? "warn" : l.state === "confirmed" ? "ok" : "";
  return [STATE_SUPPLIER[l.state] ?? l.state, tone];
}

function PoPage({ me, onChange }: { me: ExtMe; onChange: () => void }) {
  const { no = "" } = useParams();
  const [d, setD] = useState<ExtPo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [vo, setVo] = useState("");
  const [msg, setMsg] = useState("");
  const [flash, setFlash] = useState<Flash>(null);
  const [busy, setBusy] = useState(false);
  const load = () => ext.po(no).then((x) => { setD(x); setVo(x.po.vendorOrderNo ?? ""); }).catch((e) => setErr(errText(e)));
  useEffect(() => { load(); }, [no]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <div className="page"><div className="msg error">{err}</div><Link to="/supplier">Back to purchase orders</Link></div>;
  if (!d) return <div className="page"><section className="card"><div className="empty">Loading…</div></section></div>;
  const open = d.po.status === "released";
  const live = d.lines.filter((l) => !["cancelled", "received"].includes(l.state));
  const waiting = d.lines.filter((l) => l.state === "sent" || l.state === "countered");
  const answered = async (f: () => Promise<{ results?: { ok: boolean; error?: string; auto?: boolean }[] } | unknown>, ok: string) => {
    setBusy(true); setFlash(null);
    try {
      const r: any = await f();
      const bad = r?.results?.find((x: any) => !x.ok);
      setFlash(bad ? { ok: false, text: bad.error } : { ok: true, text: r?.results?.some((x: any) => x.auto) ? "Accepted: that date is inside what we accept automatically." : ok });
      await load(); onChange();
    } catch (e) { setFlash({ ok: false, text: errText(e), preview: isPreview(e) }); }
    setBusy(false);
  };
  const acceptAll = () => answered(() => ext.acceptAll(no, vo || undefined), `Accepted ${waiting.length} line${waiting.length === 1 ? "" : "s"}. Thank you.`);
  const saveVo = () => answered(() => ext.respond(no, { vendorOrderNo: vo, lines: [] }), "Saved your order number.");
  const send = async (e: FormEvent) => { e.preventDefault(); if (!msg.trim()) return; await answered(() => ext.message(no, msg), "Sent to our buyer."); setMsg(""); };
  const confirmed = d.lines.filter((l) => l.state === "confirmed").length;
  return (
    <>
      <CommandBar status={waiting.length ? `${waiting.length} of ${live.length} line${live.length === 1 ? "" : "s"} need${waiting.length === 1 ? "s" : ""} your answer` : `${confirmed} of ${live.length} line${live.length === 1 ? "" : "s"} confirmed`}>
        {open && waiting.length > 0 && <button className="cmd primary" disabled={busy} onClick={acceptAll}>{waiting.length === live.length ? "Accept the order" : `Accept the ${waiting.length} waiting line${waiting.length === 1 ? "" : "s"}`}</button>}
        <a className="cmd" href="#messages">Message our buyer</a>
      </CommandBar>
      <div className="page card-page">
        <div className="rec-title">
          <Link to="/supplier" className="crumb">Purchase orders</Link>
          <div className="line">
            <h1>Purchase order {d.po.poNo}</h1>
            <span className="meta">Ordered {usDate(d.po.orderDate)}</span>
            {d.po.buyer && <span className="meta">Buyer {d.po.buyer}</span>}
          </div>
        </div>
        {d.po.status === "on_hold" && <div className="msg warn">Our buyer is changing this order right now. You'll hear from us when it's ready to answer.</div>}
        {d.po.status === "cancelled" && <div className="msg error">This order is cancelled. Please don't ship anything on it.</div>}
        {flash && <div className={`msg ${flash.ok ? "info" : flash.preview ? "warn" : "error"}`} role="status">{flash.text}</div>}

        <div className="xp-secs">
          <XSection title="General">
            <div className="bc-fields">
              <dl>
                <dt>Order date</dt><dd>{usDate(d.po.orderDate)}</dd>
                <dt>Ship to</dt><dd>{d.po.shipTo ?? ""}</dd>
                <dt>Buyer</dt><dd>{d.po.buyer ?? ""}</dd>
              </dl>
              <dl>
                <dt><label htmlFor="vo">Your order no.</label></dt>
                <dd>{open ? <span className="xp-inline"><input id="vo" className="input sm" value={vo} maxLength={35} placeholder="Your sales order or confirmation no." onChange={(e) => setVo(e.target.value)} />
                  <button className="btn sm" disabled={busy || !vo.trim() || vo === (d.po.vendorOrderNo ?? "")} onClick={saveVo}>Save</button></span> : d.po.vendorOrderNo ?? ""}</dd>
                <dt>Status</dt><dd>{waiting.length ? `${waiting.length} line${waiting.length === 1 ? "" : "s"} need${waiting.length === 1 ? "s" : ""} your answer` : `${confirmed} of ${live.length} confirmed`}</dd>
              </dl>
            </div>
          </XSection>

          <XSection title="Lines" chips={[`${d.lines.length} line${d.lines.length === 1 ? "" : "s"}`]}>
            <div className="table-wrap">
              <table className="dgrid xp-lines">
                <thead><tr><th>Your part no.</th><th className="xp-hide-m">Our item</th><th className="xp-hide-m">Description</th><th className="r xp-hide-m">Quantity</th><th className="r xp-hide-m">Unit price</th><th className="xp-hide-m">Needed by</th><th className="xp-hide-m">Promised</th><th>Status</th><th className="xp-hide-m" /></tr></thead>
                <tbody>{d.lines.map((l) => <LineRows key={l.lineNo} l={l} po={d} open={open} busy={busy} answer={answered} />)}</tbody>
              </table>
            </div>
          </XSection>

          <XSection title="Messages" chips={[d.messages.length ? `${d.messages.length}` : ""]}>
            <div id="messages" className="sd-thread">
              {!d.messages.length && <p className="muted">Questions about this order? Ask our buyer here. We answer here and by email.</p>}
              {d.messages.map((m) => (
                <div key={m.id} className={`sd-msg ${m.side === "supplier" ? "me" : ""}`}>
                  <div className="h"><b>{m.side === "supplier" ? m.authorName : `${m.authorName}, ${d.company}`}</b><span className="muted">· {when(m.createdAt)}{m.lineNo ? ` · line ${m.lineNo}` : ""}</span></div>
                  <div className="b">{m.body}</div>
                </div>
              ))}
            </div>
            <form onSubmit={send}>
              <textarea className="textarea" rows={3} aria-label="Message to our buyer" placeholder="Write to our buyer" value={msg} onChange={(e) => setMsg(e.target.value)} />
              <div className="btn-row xp-gap"><button className="btn" disabled={busy || !msg.trim()}>Send</button></div>
            </form>
          </XSection>

          {d.history.length > 0 && <XSection title="History" open={false}>
            <ol className="sp-history">{d.history.map((h, i) => <li key={i}><span className="when">{when(h.at)}</span><span><b>{h.actorName}</b> {(EVENT[h.kind] ?? h.kind).toLowerCase()}{h.lineNo ? `, line ${h.lineNo}` : ""}</span></li>)}</ol>
          </XSection>}
        </div>
        <p className="muted sm">{me.preview ? `Preview opened by ${me.preview.by}` : `Signed in as ${me.user.email}`}</p>
      </div>
    </>
  );
}

/** One purchase line: the row, any note about it, and the change or can't-supply form when open. */
function LineRows({ l, po, open, busy, answer }: { l: Ln; po: ExtPo; open: boolean; busy: boolean; answer: (f: () => Promise<any>, ok: string) => Promise<void> }) {
  const [mode, setMode] = useState<"" | "change" | "reject">("");
  const want = l.promisedDate ?? l.requestedDate ?? "";
  const [splits, setSplits] = useState<{ date: string; qty: string }[]>([{ date: want, qty: String(l.qty) }]);
  const [price, setPrice] = useState("");
  const [reason, setReason] = useState("capacity");
  const [note, setNote] = useState("");
  const no = po.po.poNo;
  const live = open && !["received", "cancelled"].includes(l.state);
  const canChange = live && ["sent", "countered", "confirmed", "proposed"].includes(l.state) && l.outstanding > 0;
  const total = splits.reduce((s, x) => s + (Number(x.qty) || 0), 0);
  const [st, tone] = lineStatus(l);
  const cols = 9;
  const submitChange = (e: FormEvent) => {
    e.preventDefault();
    const sp: Split[] = splits.filter((x) => x.date && Number(x.qty) > 0).map((x) => ({ date: x.date, qty: Number(x.qty) }));
    answer(() => ext.respond(no, { lines: [{ lineNo: l.lineNo, action: "change", splits: sp, unitCost: price === "" ? null : Number(price), reason, note }] }), "Sent to our buyer. We'll let you know.").then(() => setMode(""));
  };
  const submitReject = (e: FormEvent) => { e.preventDefault(); answer(() => ext.respond(no, { lines: [{ lineNo: l.lineNo, action: "reject", reason, note }] }), "Thanks for telling us. Our buyer will be in touch.").then(() => setMode("")); };
  const acts = live && mode === "" && ((l.state === "sent" || l.state === "countered") || l.askConfirm || canChange) ? <span className="btn-row">
    {(l.state === "sent" || l.state === "countered") && <button className="btn sm primary" disabled={busy} onClick={() => answer(() => ext.respond(no, { lines: [{ lineNo: l.lineNo, action: "accept" }] }), l.state === "countered" ? "Accepted our buyer's proposal." : `Accepted line ${l.lineNo}.`)}>{l.state === "countered" ? "Accept proposal" : "Accept"}</button>}
    {l.askConfirm && <button className="btn sm primary" disabled={busy} onClick={() => answer(() => ext.respond(no, { lines: [{ lineNo: l.lineNo, action: "confirm" }] }), "Thanks, still on track.")}>Still on track</button>}
    {canChange && <button className="btn sm" disabled={busy} onClick={() => setMode("change")}>{l.state === "confirmed" ? "Change date" : "Propose change"}</button>}
    {canChange && l.state !== "confirmed" && !l.open && <button className="btn sm" disabled={busy} onClick={() => setMode("reject")}>Can't supply</button>}
  </span> : null;
  const notes: ReactNode[] = [];
  if (l.changes?.length) notes.push(<span key="c">We changed this line: {l.changes.map((c) => c.field === "qty" ? `quantity ${fmtQty(Number(c.from))} → ${fmtQty(Number(c.to))}` : c.field === "unitCost" ? `price ${c.from} → ${c.to}` : c.field === "requestedDate" ? `needed by ${usDate(String(c.from))} → ${usDate(String(c.to))}` : c.field === "line" ? "new line" : c.field).join("; ")}. Please confirm again.</span>);
  if (l.counter) notes.push(<span key="k"><b>Our buyer proposes</b> {l.counter.splits.map((x) => `${fmtQty(x.qty)} by ${usDate(x.date)}`).join(", ")}{l.counter.unitCost !== null ? ` at ${money(l.counter.unitCost, po.po.currency)}` : ""}.{l.counter.note && <> “{l.counter.note}”, {l.counter.byName}</>}</span>);
  if (l.open) notes.push(<span key="o">{l.open.kind === "reject" ? "You told us you can't supply this line" : <>You proposed {l.open.splits.map((x) => `${fmtQty(x.qty)} by ${usDate(x.date)}`).join(", ")}{l.open.unitCost !== null ? ` at ${money(l.open.unitCost, po.po.currency)}` : ""}</>}. Our buyer will answer soon.</span>);
  if (!l.open && l.last && l.last.side === "supplier" && l.last.decidedAt && (l.state === "sent" || l.state === "confirmed") && l.last.status !== "accepted")
    notes.push(<span key="l">Our buyer couldn't take your {l.last.kind === "reject" ? "answer" : "change"}{l.last.decisionNote ? `: “${l.last.decisionNote}”` : "."}</span>);
  return (
    <>
      <tr className={l.state === "cancelled" ? "dim" : ""}>
        <td><b>{l.vendorItemNo || l.itemNo}</b><div className="xp-m muted sm">{[l.description, `${fmtQty(l.qty)} ${l.uom ?? ""}`.trim(), `needed ${usDate(l.requestedDate)}`, l.promisedDate ? `promised ${usDate(l.promisedDate)}` : "", money(l.unitCost, po.po.currency)].filter(Boolean).join(" · ")}</div></td>
        <td className="xp-hide-m">{l.itemNo}</td>
        <td className="xp-hide-m xp-desc" title={l.description ?? ""}>{l.description}</td>
        <td className="r xp-hide-m">{fmtQty(l.qty)} {l.uom}{l.received ? <div className="muted sm">{fmtQty(l.received)} received</div> : null}</td>
        <td className="r xp-hide-m">{money(l.unitCost, po.po.currency)}</td>
        <td className="xp-hide-m">{usDate(l.requestedDate)}</td>
        <td className="xp-hide-m">{l.promisedDate ? <b>{usDate(l.promisedDate)}</b> : <span className="muted">–</span>}</td>
        <td><span className={`status ${tone}`}>{st}</span></td>
        <td className="xp-acts xp-hide-m">{acts}</td>
      </tr>
      {acts && <tr className="xp-sub xp-m-row"><td colSpan={cols}>{acts}</td></tr>}
      {notes.length > 0 && <tr className="xp-sub"><td colSpan={cols}><div className="msg info xp-note">{notes}</div></td></tr>}
      {mode === "change" && (
        <tr className="xp-sub"><td colSpan={cols}>
          <form className="xp-form" onSubmit={submitChange} aria-label={`Propose a change to line ${l.lineNo}`}>
            <div className="xp-form-h">Propose a change to {l.vendorItemNo || l.itemNo}</div>
            <div className="field"><span className="label">Deliveries</span>
              {splits.map((s, i) => (
                <div key={i} className="xp-split">
                  <input className="input" type="date" required aria-label={`Delivery ${i + 1} date`} value={s.date} onChange={(e) => setSplits(splits.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} />
                  <input className="input num" type="number" min={0} step="any" required aria-label={`Delivery ${i + 1} quantity`} value={s.qty} onChange={(e) => setSplits(splits.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))} />
                  <span className="muted">{l.uom}</span>
                  {i === 0 && s.date && want && daysBetween(want, s.date) ? <span className={daysBetween(want, s.date)! > 0 ? "bad" : "muted"}>{daysBetween(want, s.date)! > 0 ? "+" : ""}{daysBetween(want, s.date)} days</span> : null}
                  {splits.length > 1 && <button type="button" className="btn sm" onClick={() => setSplits(splits.filter((_, j) => j !== i))}>Remove</button>}
                </div>
              ))}
              <div><button type="button" className="btn sm" onClick={() => setSplits([...splits, { date: "", qty: "" }])}>Split into another delivery</button></div>
              {total !== l.qty && <span className="hint">Total {fmtQty(total)} {l.uom}, ordered {fmtQty(l.qty)}.</span>}
            </div>
            <div className="xp-cols">
              <div className="field"><label htmlFor={`pr-${l.lineNo}`}>New unit price (optional)</label><input id={`pr-${l.lineNo}`} className="input" type="number" min={0} step="any" placeholder={String(l.unitCost)} value={price} onChange={(e) => setPrice(e.target.value)} /></div>
              <div className="field"><label htmlFor={`rs-${l.lineNo}`}>Reason</label><select id={`rs-${l.lineNo}`} className="select" value={reason} onChange={(e) => setReason(e.target.value)}>{Object.entries(REASON).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
            </div>
            <div className="field"><label htmlFor={`nt-${l.lineNo}`}>Note for our buyer</label><input id={`nt-${l.lineNo}`} className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything that helps us plan" /></div>
            <div className="btn-row"><button className="btn primary" disabled={busy}>Send proposal</button><button type="button" className="btn" onClick={() => setMode("")}>Cancel</button></div>
          </form>
        </td></tr>
      )}
      {mode === "reject" && (
        <tr className="xp-sub"><td colSpan={cols}>
          <form className="xp-form" onSubmit={submitReject} aria-label={`Can't supply line ${l.lineNo}`}>
            <div className="xp-form-h">Can't supply {l.vendorItemNo || l.itemNo}</div>
            <div className="xp-cols">
              <div className="field"><label htmlFor={`rr-${l.lineNo}`}>Reason</label><select id={`rr-${l.lineNo}`} className="select" value={reason} onChange={(e) => setReason(e.target.value)}>{Object.entries(REASON).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
              <div className="field"><label htmlFor={`rn-${l.lineNo}`}>What happened</label><input id={`rn-${l.lineNo}`} className="input" required value={note} onChange={(e) => setNote(e.target.value)} placeholder="So our buyer can find another way" /></div>
            </div>
            <div className="btn-row"><button className="btn primary" disabled={busy}>Send</button><button type="button" className="btn" onClick={() => setMode("")}>Cancel</button></div>
          </form>
        </td></tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Team
// ---------------------------------------------------------------------------------------------------------------
function Team({ me, onChange }: { me: ExtMe; onChange: () => void }) {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof ext.team>> | null>(null);
  const [add, setAdd] = useState({ email: "", name: "", admin: false });
  const [adding, setAdding] = useState(false);
  const [flash, setFlash] = useState<Flash>(null);
  const load = () => ext.team().then(setRows).catch((e) => setFlash({ ok: false, text: errText(e) }));
  useEffect(() => { load(); }, []);
  const run = async (f: () => Promise<unknown>, ok: string) => { setFlash(null); try { await f(); setFlash({ ok: true, text: ok }); load(); onChange(); return true; } catch (e) { setFlash({ ok: false, text: errText(e), preview: isPreview(e) }); return false; } };
  const admin = me.user.role === "admin";
  return (
    <>
      <CommandBar status={rows ? `${rows.length} ${rows.length === 1 ? "person" : "people"}` : "Loading…"}>
        {admin && <button className="cmd primary" onClick={() => setAdding(true)}>Add a person</button>}
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Team</h1><span className="sub">Everyone at {me.supplier.name} who can answer our purchase orders. {admin ? "As admin, you decide who has access." : "Your admin decides who has access."}</span></div>
        {flash && <div className={`msg ${flash.ok ? "info" : flash.preview ? "warn" : "error"}`} role="status">{flash.text}</div>}
        {adding && admin && (
          <section className="card xp-gap-b">
            <div className="card-head"><h2>Add a person</h2><button type="button" className="btn sm" onClick={() => setAdding(false)}>Close</button></div>
            <form className="card-body xp-body" onSubmit={async (e) => { e.preventDefault(); if (await run(() => ext.addTeam({ email: add.email, name: add.name || undefined, role: add.admin ? "admin" : "user" }), `Invited ${add.email}. They'll get a sign-in link.`)) { setAdd({ email: "", name: "", admin: false }); setAdding(false); } }}>
              <div className="xp-cols">
                <div className="field"><label htmlFor="tm-e">Email</label><input id="tm-e" className="input" type="email" required value={add.email} onChange={(e) => setAdd({ ...add, email: e.target.value })} /></div>
                <div className="field"><label htmlFor="tm-n">Name</label><input id="tm-n" className="input" value={add.name} onChange={(e) => setAdd({ ...add, name: e.target.value })} /></div>
              </div>
              <label className="xp-check"><input type="checkbox" checked={add.admin} onChange={(e) => setAdd({ ...add, admin: e.target.checked })} /> Admin: can add and remove people</label>
              <div className="btn-row"><button className="btn primary">Send invite</button></div>
            </form>
          </section>
        )}
        <section className="card xp-gap-b">
          {!rows ? <div className="empty">Loading…</div> : !rows.length ? <div className="empty">Nobody has access yet.</div> : (
            <div className="table-wrap"><table className="dgrid">
              <thead><tr><th>Name</th><th className="xp-hide-m">Email</th><th>Role</th><th className="xp-hide-m">Signed in</th><th /></tr></thead>
              <tbody>{rows.map((u) => (
                <tr key={u.id}>
                  <td><b>{u.name || u.email}</b><div className="xp-m muted sm">{u.email}</div></td>
                  <td className="xp-hide-m">{u.email}</td>
                  <td>{u.role === "admin" ? "Admin" : "User"}</td>
                  <td className="xp-hide-m">{u.status === "active" ? (u.lastSignInAt ? usDate(u.lastSignInAt) : "Yes") : <span className="muted">Invited</span>}</td>
                  <td className="xp-acts">{admin && u.id !== me.user.id && <span className="btn-row">
                    {u.role !== "admin" ? <button className="btn sm" onClick={() => run(() => ext.updateTeam(u.id, { role: "admin" }), `${u.email} is an admin.`)}>Make admin</button> : <button className="btn sm" onClick={() => run(() => ext.updateTeam(u.id, { role: "user" }), `${u.email} is a user.`)}>Make user</button>}
                    <button className="btn sm" onClick={() => run(() => ext.updateTeam(u.id, { status: "removed" }), `Removed ${u.email}.`)}>Remove</button>
                  </span>}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </section>
        <section className="card">
          <div className="card-head"><h2>My emails</h2></div>
          <div className="card-body xp-body">
            <div className="field"><label htmlFor="tm-notify">Email me about our orders</label>
              <select id="tm-notify" className="select xp-w" value={me.user.notify} onChange={(e) => run(() => ext.saveMe({ notify: e.target.value }), "Saved.")}>
                <option value="instant">As they happen</option><option value="digest">Once a day, in a summary</option><option value="none">Never; I'll sign in to check</option>
              </select>
              <span className="hint">New and cancelled orders are always emailed straight away, unless you choose never.</span>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
